import { useEffect, useRef, useState } from 'react';
import { login, loginWithTOTP } from '../../lib/panelApi';

type Props = {
  onLoggedIn: (accessToken: string) => void;
};

type Stage =
  | { step: 'credentials' }
  | { step: 'totp'; totpToken: string; expiresAt: string };

export function LoginView({ onLoggedIn }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<Stage>({ step: 'credentials' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const codeRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (stage.step === 'totp') {
      codeRef.current?.focus();
    }
  }, [stage.step]);

  function backToCredentials() {
    setStage({ step: 'credentials' });
    setCode('');
    setError(null);
  }

  async function onSubmitCredentials(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const em = email.trim();
    if (!em || !password) {
      setError('Email and password are required.');
      return;
    }
    setLoading(true);
    try {
      const res = await login(em, password);
      if (res.requires_totp && res.totp_token && res.totp_expires_at) {
        setStage({ step: 'totp', totpToken: res.totp_token, expiresAt: res.totp_expires_at });
        return;
      }
      if (res.access_token) {
        onLoggedIn(res.access_token);
        return;
      }
      setError('Unexpected response from server.');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Login failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function onSubmitTOTP(e: React.FormEvent) {
    e.preventDefault();
    if (stage.step !== 'totp') return;
    setError(null);
    if (!/^\d{6}$/.test(code)) {
      setError('Authenticator code must be 6 digits.');
      return;
    }
    setLoading(true);
    try {
      const res = await loginWithTOTP(stage.totpToken, code);
      if (res.access_token) {
        onLoggedIn(res.access_token);
        return;
      }
      setError('Unexpected response from server.');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Login failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="card" style={{ maxWidth: 520, margin: '0 auto' }}>
      <div className="sectionTitle">{stage.step === 'credentials' ? 'Admin Login' : 'Two-factor verification'}</div>
      <div className="subtitle" style={{ marginBottom: 12 }}>
        {stage.step === 'credentials'
          ? 'Sign in with your Gmail account to manage books and content.'
          : `Enter the 6-digit code from your authenticator app for ${email}.`}
      </div>

      {stage.step === 'credentials' ? (
        <form onSubmit={onSubmitCredentials}>
          <div className="field">
            <div className="label">Email</div>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@gmail.com"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
            />
          </div>

          <div className="field">
            <div className="label">Password</div>
            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              type="password"
              autoComplete="current-password"
            />
          </div>

          {error ? (
            <div style={{ marginTop: 12 }}>
              <div className="errorBox">{error}</div>
            </div>
          ) : null}

          <div className="row" style={{ marginTop: 14 }}>
            <button className="btn btn-primary" type="submit" disabled={loading}>
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={onSubmitTOTP}>
          <div className="field">
            <div className="label">6-digit code</div>
            <input
              ref={codeRef}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
              placeholder="123456"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              style={{ letterSpacing: '0.3em', fontVariantNumeric: 'tabular-nums', fontSize: 18 }}
            />
            <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
              Codes refresh every 30 seconds.
            </div>
          </div>

          {error ? (
            <div style={{ marginTop: 12 }}>
              <div className="errorBox">{error}</div>
            </div>
          ) : null}

          <div className="row" style={{ marginTop: 14, gap: 8 }}>
            <button className="btn btn-primary" type="submit" disabled={loading}>
              {loading ? 'Verifying…' : 'Verify and sign in'}
            </button>
            <button className="btn" type="button" onClick={backToCredentials} disabled={loading}>
              Back
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
